import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPen, faCheck, faSpinner, faCalendar, faDollarSign, faFileInvoice, faBuilding, faUser } from '@fortawesome/free-solid-svg-icons';
import { getContaPagarById, baixarContaPagar } from '../../../services/contaPagarService';
import { getById as getContaById } from '../../../services/contaService';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (value) => {
  if (!value) return '-';
  try {
    if (value instanceof Date) return value.toLocaleDateString('pt-BR');
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
    }
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return String(value);
    return dt.toLocaleDateString('pt-BR');
  } catch (err) {
    return String(value);
  }
};

const ContaPagarView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contaPagar, setContaPagar] = useState(null);
  const [conta, setConta] = useState(null);
  const [baixando, setBaixando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const contaPagarData = await getContaPagarById(id);

        if (!contaPagarData) {
          setError('Conta a pagar não encontrada');
          return;
        }

        setContaPagar(contaPagarData);

        // Carregar dados da conta
        if (contaPagarData.conta) {
          const contaData = await getContaById(contaPagarData.conta);
          setConta(contaData);
        }
        setError(null);
      } catch (err) {
        setError(err.message || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadData();
    }
  }, [id]);

  const handleBaixarConta = async () => {
    if (window.confirm('Confirma a baixa desta conta?')) {
      setBaixando(true);
      try {
        const dataPagamento = new Date().toISOString().split('T')[0];
        await baixarContaPagar(id, dataPagamento, 'usuario.atual'); // TODO: pegar usuário logado
        
        // Recarregar dados
        const contaPagarData = await getContaPagarById(id);
        setContaPagar(contaPagarData);
      } catch (err) {
        alert('Erro ao baixar conta');
        console.error(err);
      } finally {
        setBaixando(false);
      }
    }
  };

  const handleBack = () => {
    navigate('/financeiro/contas-pagar');
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !contaPagar) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error || 'Conta a pagar não encontrada'}
        </div>
        <button
          onClick={handleBack}
          className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
        >
          Voltar
        </button>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-4 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
          </button>
          <h2 className="text-xl font-bold">Detalhes da Conta a Pagar #{contaPagar.id}</h2>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/financeiro/contas-pagar/${id}/edit/`}
            className="px-4 py-2 rounded bg-blue-600 text-white font-semibold shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPen} />
            <span>Editar</span>
          </Link>

          {contaPagar.pago === 'Não' && (
            <button
              onClick={handleBaixarConta}
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
              disabled={baixando}
            >
              {baixando ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faCheck} />
              )}
              <span>{baixando ? 'Baixando...' : 'Baixar Conta'}</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Informações Principais */}
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Informações Principais</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faFileInvoice} className="text-blue-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Descrição:</span>
                  <p className="text-gray-800">{contaPagar.descricao}</p>
                </div>
              </div>

              <div className="flex items-start">
                <FontAwesomeIcon icon={faCalendar} className="text-green-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Data de Vencimento:</span>
                  <p className="text-gray-800">
                    {formatDate(contaPagar.dataVencimento)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <FontAwesomeIcon icon={faDollarSign} className="text-yellow-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Valor:</span>
                  <p className="text-gray-800 font-semibold">
                    {currencyFormatter.format(Number(contaPagar.valor) || 0)}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <FontAwesomeIcon icon={faBuilding} className="text-purple-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Conta:</span>
                  <p className="text-gray-800">
                    {conta ? `${conta.codigo} - ${conta.descricao}` : contaPagar.conta}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <FontAwesomeIcon icon={faFileInvoice} className="text-indigo-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Parcela:</span>
                  <p className="text-gray-800">
                    {contaPagar.parcela} de {contaPagar.totalParcelas}
                  </p>
                </div>
              </div>

              {contaPagar.dataPagamento && (
                <div className="flex items-start">
                  <FontAwesomeIcon icon={faCalendar} className="text-green-600 mt-1 mr-3" />
                  <div>
                    <span className="text-sm font-medium text-gray-600">Data de Pagamento:</span>
                    <p className="text-gray-800">
                      {formatDate(contaPagar.dataPagamento)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Informações de Auditoria */}
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Informações de Auditoria</h3>
            
            <div className="space-y-3">
              <div className="flex items-start">
                <FontAwesomeIcon icon={faUser} className="text-blue-500 mt-1 mr-3" />
                <div>
                  <span className="text-sm font-medium text-gray-600">Cadastrado por:</span>
                  <p className="text-gray-800">{contaPagar.cadastradoPor}</p>
                </div>
              </div>

              <div>
                <span className="text-sm font-medium text-gray-600">Data de cadastro:</span>
                <p className="text-gray-800">
                  {formatDate(contaPagar.dataInclusao)}
                </p>
              </div>

              {contaPagar.alteradoPor && (
                <>
                  <hr className="my-3" />
                  <div>
                    <span className="text-sm font-medium text-gray-600">Última alteração por:</span>
                    <p className="text-gray-800">{contaPagar.alteradoPor}</p>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-gray-600">Data da última alteração:</span>
                    <p className="text-gray-800">
                      {formatDate(contaPagar.dataAlteracao)}
                    </p>
                  </div>
                </>
              )}

              {contaPagar.baixadoPor && (
                <>
                  <hr className="my-3" />
                  <div>
                    <span className="text-sm font-medium text-gray-600">Baixado por:</span>
                    <p className="text-gray-800">{contaPagar.baixadoPor}</p>
                  </div>

                  <div>
                    <span className="text-sm font-medium text-gray-600">Data da baixa:</span>
                      <p className="text-gray-800">
                      {formatDate(contaPagar.dataBaixa)}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Card de Resumo */}
          <div className={`${contaPagar.pago === 'Sim' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border p-4 rounded-lg`}>
            <h3 className={`text-lg font-semibold mb-2 ${contaPagar.pago === 'Sim' ? 'text-green-800' : 'text-red-800'}`}>
              Status da Conta
            </h3>
            <div className={`text-sm ${contaPagar.pago === 'Sim' ? 'text-green-700' : 'text-red-700'}`}>
              <p className="mb-1">
                <span className="font-medium">Status:</span> {contaPagar.pago === 'Sim' ? 'Pago' : 'Pendente'}
              </p>
              <p className="mb-1">
                <span className="font-medium">Valor:</span> {currencyFormatter.format(Number(contaPagar.valor) || 0)}
              </p>
              <p className="mb-1">
                <span className="font-medium">Vencimento:</span> {formatDate(contaPagar.dataVencimento)}
              </p>
              <p>
                <span className="font-medium">Parcela:</span> {contaPagar.parcela} de {contaPagar.totalParcelas}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="flex justify-end mt-6 pt-6 border-t border-gray-200">
        <div className="flex gap-2">
          <button
            onClick={handleBack}
            className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors"
          >
            Voltar
          </button>
          <Link
            to={`/financeiro/contas-pagar/${id}/edit`}
            className="px-4 py-2 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPen} />
            Editar Conta
          </Link>
          {contaPagar.pago === 'Não' && (
            <button
              onClick={handleBaixarConta}
              className="px-4 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
              disabled={baixando}
            >
              {baixando ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faCheck} />
              )}
              {baixando ? 'Baixando...' : 'Baixar Conta'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContaPagarView;
